import { useCallback, useEffect, useState } from 'react'
import { Upload, Loader2, Check, AlertTriangle } from 'lucide-react'
import { useTeamsDataApi } from './useTeamsDataApi'

// CSV upload pane.
//
// Two adapters in Phase 1:
//   canonical_csv — editor's own hand-curated format with the documented
//                   column schema (org_id, sport, season, team_code,
//                   team_name, player_name, plus optionals).
//   pihl_stats    — the format produced by the existing Python scraper at
//                   tbtv-teams-data/PIHL/pihl_roster_from_stats_enriched.py
//
// Flow: pick file → dry-run preview (parse only, no DB) → confirm → commit.
export default function CsvUploader() {
  const api = useTeamsDataApi()
  const [file, setFile] = useState(null)
  const [sourceKind, setSourceKind] = useState('pihl_stats')
  const [season, setSeason] = useState('2025-26')
  const [preview, setPreview] = useState(null)
  const [result, setResult] = useState(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [uploads, setUploads] = useState([])

  const refreshUploads = useCallback(() => {
    api.listUploads({ limit: 20 }).then(r => setUploads(r?.uploads || [])).catch(() => {})
  }, [api])

  useEffect(() => { refreshUploads() }, [refreshUploads])

  async function doDryRun() {
    if (!file) return alert('pick a file first')
    setBusy(true); setErr(null); setPreview(null); setResult(null)
    try {
      const r = await api.uploadCsv(file, { source_kind: sourceKind, season, dry_run: true })
      setPreview(r)
    } catch (e) {
      setErr(e?.message || String(e))
    } finally {
      setBusy(false)
    }
  }

  async function commit() {
    if (!file) return
    if (!confirm(`Commit ${preview?.record_count || 0} records to the live roster?`)) return
    setBusy(true); setErr(null); setResult(null)
    try {
      const r = await api.uploadCsv(file, { source_kind: sourceKind, season })
      setResult(r)
      refreshUploads()
    } catch (e) {
      setErr(e?.message || String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <div style={card}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
          <Field label="Source kind">
            <select value={sourceKind} onChange={e => setSourceKind(e.target.value)} style={inp}>
              <option value="pihl_stats">pihl_stats (PIHL Python scraper output)</option>
              <option value="acha_stats">acha_stats (ACHA Python scraper — CHE / CHMA / any ACHA conference)</option>
              <option value="canonical_csv">canonical_csv (hand-curated)</option>
            </select>
          </Field>
          <Field label="Season">
            <input value={season} onChange={e => setSeason(e.target.value)} placeholder="2025-26" style={inp} />
          </Field>
          <Field label="CSV file">
            <input type="file" accept=".csv,text/csv" onChange={e => { setFile(e.target.files?.[0] || null); setPreview(null); setResult(null) }}
              style={{ ...inp, padding: 4 }} />
          </Field>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={doDryRun} disabled={!file || busy}
            style={{ ...btnSecondary, opacity: !file || busy ? 0.5 : 1 }}>
            {busy ? <Loader2 size={12} className="spin" /> : <Upload size={12} />} Preview (dry-run)
          </button>
          <button onClick={commit} disabled={!preview || busy}
            style={{ ...btnPrimary, opacity: !preview || busy ? 0.5 : 1 }}>
            <Check size={12} /> Commit upload
          </button>
        </div>
        {sourceKind === 'canonical_csv' && (
          <div style={{ marginTop: 8, fontSize: 11, color: '#94a3b8' }}>
            <strong>Canonical CSV columns:</strong>{' '}
            <code>org_id, sport, season, team_code, team_name, player_name</code> required;{' '}
            <code>jersey_number, position, division, first_name, last_name, source_url</code> optional.
          </div>
        )}
        {sourceKind === 'pihl_stats' && (
          <div style={{ marginTop: 8, fontSize: 11, color: '#94a3b8' }}>
            <strong>PIHL stats CSV:</strong> output of{' '}
            <code>pihl_roster_from_stats_enriched.py</code>. org_id forced to{' '}
            <code>pihl</code>, sport to <code>hockey</code>. Season comes from this form field.
          </div>
        )}
        {sourceKind === 'acha_stats' && (
          <div style={{ marginTop: 8, fontSize: 11, color: '#94a3b8' }}>
            <strong>ACHA stats CSV:</strong> output of{' '}
            <code>tbtv-teams-data/acha_stats_scraper.py</code> (parameterized for any
            ACHA conference). org_id is read from each row (set by the scraper's
            <code> --org-id</code> flag), sport forced to <code>hockey</code>, season
            comes from this form field. Team names auto-stripped of the
            <code> MDx </code> division prefix; per-org team_code maps seeded in
            <code> acha-stats.js</code>; unknown teams get a derived initials code.
            <br /><br />
            Old CHE-only CSVs (without an <code>org_id</code> column) still upload via
            the fallback <code>conference_name</code> sniffer.
          </div>
        )}
      </div>

      {err && (
        <div style={{ ...card, background: '#2a1414', borderColor: '#5a1f1f' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#fca5a5' }}>
            <AlertTriangle size={12} /> <strong>Upload failed</strong>
          </div>
          <pre style={{ marginTop: 6, color: '#fca5a5', fontSize: 11, whiteSpace: 'pre-wrap' }}>{err}</pre>
        </div>
      )}

      {preview && !result && (
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <strong style={{ color: '#fde68a' }}>Dry-run preview</strong>
            <span style={{ color: '#888', fontSize: 12 }}>{preview.record_count} records · {(preview.parse_errors || []).length} parse errors</span>
          </div>
          {(preview.parse_errors || []).length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <details>
                <summary style={{ cursor: 'pointer', color: '#fca5a5', fontSize: 11 }}>
                  Show {preview.parse_errors.length} parse error(s)
                </summary>
                <pre style={{ marginTop: 4, fontSize: 10, color: '#fca5a5', maxHeight: 160, overflowY: 'auto' }}>{JSON.stringify(preview.parse_errors, null, 2)}</pre>
              </details>
            </div>
          )}
          <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>First 10 records:</div>
          <pre style={{ background: '#020617', padding: 8, borderRadius: 4, fontSize: 10, color: '#cbd5e1', maxHeight: 280, overflowY: 'auto' }}>
            {JSON.stringify(preview.preview, null, 2)}
          </pre>
        </div>
      )}

      {result && (
        <div style={{ ...card, background: '#0c1c14', borderColor: '#1e3a2f' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <Check size={14} style={{ color: '#86efac' }} />
            <strong style={{ color: '#86efac' }}>Upload committed</strong>
          </div>
          <div style={{ fontSize: 12, color: '#cbd5e1' }}>
            upload_id: <code>{result.upload_id}</code> · {result.record_count} records ·{' '}
            <span style={{ color: '#86efac' }}>{result.inserted} inserted</span> ·{' '}
            <span style={{ color: '#7dd3fc' }}>{result.updated} updated</span> ·{' '}
            <span style={{ color: '#fca5a5' }}>{result.skipped} skipped</span>
          </div>
          {result.stats_upserted > 0 && (
            <div style={{ fontSize: 12, color: '#cbd5e1', marginTop: 4 }}>
              Season stats:{' '}
              <span style={{ color: '#86efac' }}>{result.stats_upserted} upserted</span>
            </div>
          )}
          {result.errors && result.errors.length > 0 && (
            <details style={{ marginTop: 6 }}>
              <summary style={{ cursor: 'pointer', color: '#fca5a5', fontSize: 11 }}>
                {result.errors.length} row error(s)
              </summary>
              <pre style={{ marginTop: 4, fontSize: 10, color: '#fca5a5', maxHeight: 160, overflowY: 'auto' }}>{JSON.stringify(result.errors, null, 2)}</pre>
            </details>
          )}
        </div>
      )}

      <h3 style={{ fontSize: 13, marginTop: 20, marginBottom: 8, color: '#dbe5f5' }}>Recent uploads</h3>
      <div style={{ border: '1px solid #1e293b', borderRadius: 6, overflow: 'hidden' }}>
        <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#020617', color: '#94a3b8' }}>
              <th style={th}>ID</th>
              <th style={th}>Uploaded</th>
              <th style={th}>Uploader</th>
              <th style={th}>Org</th>
              <th style={th}>Sport</th>
              <th style={th}>Season</th>
              <th style={th}>Kind</th>
              <th style={th}>File</th>
              <th style={{ ...th, textAlign: 'right' }}>In</th>
              <th style={{ ...th, textAlign: 'right' }}>Ins</th>
              <th style={{ ...th, textAlign: 'right' }}>Upd</th>
              <th style={{ ...th, textAlign: 'right' }}>Skip</th>
            </tr>
          </thead>
          <tbody>
            {uploads.map(u => (
              <tr key={u.upload_id} style={{ borderTop: '1px solid #1e293b' }}>
                <td style={td}>{u.upload_id}</td>
                <td style={td}>{u.uploaded_at}</td>
                <td style={td}>{u.uploader}</td>
                <td style={td}>{u.org_id}</td>
                <td style={td}>{u.sport}</td>
                <td style={td}>{u.season}</td>
                <td style={td}>{u.source_kind}</td>
                <td style={{ ...td, color: '#888', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={u.raw_filename}>{u.raw_filename || '—'}</td>
                <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace' }}>{u.row_count_input}</td>
                <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', color: '#86efac' }}>{u.row_count_inserted}</td>
                <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', color: '#7dd3fc' }}>{u.row_count_updated}</td>
                <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', color: '#fca5a5' }}>{u.row_count_skipped}</td>
              </tr>
            ))}
            {uploads.length === 0 && (
              <tr><td colSpan={12} style={{ ...td, color: '#888', textAlign: 'center', padding: 16 }}>No uploads yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function Field({ label, children }) {
  return <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: '#94a3b8' }}>{label}{children}</label>
}

const card = { background: '#0d1622', border: '1px solid #1e293b', borderRadius: 6, padding: 12, marginBottom: 12 }
const inp = { padding: '6px 8px', background: '#020617', border: '1px solid #334155', color: '#dbe5f5', borderRadius: 3, fontSize: 12 }
const btnPrimary = { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 12px', background: '#2563eb', border: 'none', color: '#fff', borderRadius: 4, fontSize: 12, cursor: 'pointer' }
const btnSecondary = { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 12px', background: 'transparent', border: '1px solid #334155', color: '#cbd5e1', borderRadius: 4, fontSize: 12, cursor: 'pointer' }
const th = { padding: '6px 10px', textAlign: 'left', fontSize: 10, fontWeight: 600 }
const td = { padding: '6px 10px', color: '#cbd5e1' }
